/**
 * Storefront product-detail (PDP) interactivity:
 *   - image gallery thumbnail switching + hover zoom
 *   - quantity stepper
 *   - add-to-cart (the saved cart) with the header badge
 *   - related-product quick add
 *   - "Notify me when it's back in stock" (StockAlertClient)
 *   - "Recently viewed": records this view, shows the row at the bottom (RecentlyViewed)
 *
 * Content is server-rendered in StorefrontProductIndex.cshtml; this only wires
 * up the interactive bits.
 */
import { addToCart, mountCartBadge, checkQuantity } from "../Cart/StorefrontCart";
import { showToast } from "../Toast/StorefrontToast";
import { markSavedWishlistButtons, toggleWishlist } from "../Wishlist/WishlistClient";
import { hydrateStockAlerts } from "../StockAlert/StockAlertClient";
import { mountRecentlyViewed, recordView } from "../RecentlyViewed/RecentlyViewed";

function initGallery(): void {
    const mainImg = document.getElementById("sf-gallery-image") as HTMLImageElement | null;
    if (!mainImg) return;

    const thumbs = Array.from(document.querySelectorAll<HTMLButtonElement>(".sf-thumb"));
    for (const thumb of thumbs) {
        thumb.addEventListener("click", () => {
            const src = thumb.getAttribute("data-image");
            if (!src) return;
            mainImg.src = src;
            thumbs.forEach(t => t.classList.remove("is-active"));
            thumb.classList.add("is-active");
        });
    }

    // Simple hover zoom: shift transform-origin to the cursor.
    const main = mainImg.parentElement;
    if (main) {
        main.addEventListener("mousemove", (e: MouseEvent) => {
            const rect = main.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / rect.width) * 100;
            const y = ((e.clientY - rect.top) / rect.height) * 100;
            mainImg.style.transformOrigin = `${x}% ${y}%`;
        });
        main.addEventListener("mouseenter", () => main.classList.add("is-zooming"));
        main.addEventListener("mouseleave", () => main.classList.remove("is-zooming"));
    }
}

function initQty(): number {
    const wrap = document.querySelector<HTMLElement>("[data-qty]");
    const input = wrap?.querySelector<HTMLInputElement>(".sf-qty-input");
    const warn = document.querySelector<HTMLElement>("[data-qty-warn]");
    const incBtn = wrap?.querySelector<HTMLButtonElement>(".sf-qty-inc");
    if (!wrap || !input) return 1;

    const max = parseInt(input.max, 10) || 999;

    const clamp = () => {
        let v = parseInt(input.value, 10);
        if (isNaN(v) || v < 1) v = 1;
        const limited = v > max;
        if (limited) v = max;
        input.value = String(v);

        if (incBtn) incBtn.disabled = v >= max;
        if (warn) {
            warn.hidden = !limited;
            warn.textContent = limited ? `Only ${max} available — quantity reduced` : "";
        }
        return v;
    };

    wrap.querySelector(".sf-qty-dec")?.addEventListener("click", () => {
        input.value = String(Math.max(1, (parseInt(input.value, 10) || 1) - 1));
        clamp();
    });
    wrap.querySelector(".sf-qty-inc")?.addEventListener("click", () => {
        input.value = String((parseInt(input.value, 10) || 1) + 1);
        clamp();
    });
    input.addEventListener("change", clamp);
    return clamp();
}

function flash(button: HTMLElement, text: string): void {
    const original = button.textContent;
    button.textContent = text;
    button.classList.add("is-added");
    window.setTimeout(() => {
        button.textContent = original;
        button.classList.remove("is-added");
    }, 1400);
}

function initAddToCart(): void {
    const addBtn = document.getElementById("sf-add-to-cart") as HTMLButtonElement | null;
    const qtyInput = document.querySelector<HTMLInputElement>(".sf-qty-input");
    const warn = document.querySelector<HTMLElement>("[data-qty-warn]");

    addBtn?.addEventListener("click", async () => {
        const partId = parseInt(addBtn.getAttribute("data-part-id") || "0", 10);
        if (!partId) return;
        const qty = Math.max(1, parseInt(qtyInput?.value || "1", 10) || 1);

        addBtn.disabled = true;
        try {
            const check = await checkQuantity(partId, qty);
            if (check && !check.Success) return; // part not found — nothing to add

            if (check?.IsOutOfStock) {
                if (warn) { warn.hidden = false; warn.textContent = "Out of stock"; }
                revealStockAlert();
                return;
            }

            // The live count is fresher than the one the page rendered with.
            const lowStock = document.querySelector<HTMLElement>("[data-low-stock]");
            if (lowStock && check) lowStock.hidden = !check.IsLowStock;

            const finalQty = check?.IsPartsExceedingStock ? Math.max(1, check.AvailableQuantity) : qty;
            if (check?.IsPartsExceedingStock && qtyInput) qtyInput.value = String(finalQty);
            if (warn) {
                // A live-check warning (couldn't reach the supplier API — doesn't block the
                // add) takes priority over the plain "quantity reduced" notice, since it
                // means the reduced number itself might not even be accurate.
                const message = check?.Warning || (check?.IsPartsExceedingStock ? `Only ${finalQty} available — quantity reduced` : "");
                warn.hidden = !message;
                warn.textContent = message;
            }

            await addToCart(partId, finalQty);
            flash(addBtn, "✓ Added");
        } catch (e) {
            showToast((e as Error).message, "error");
        } finally {
            addBtn.disabled = false;
        }
    });

    // Add to Wishlist — saves the current part + selected quantity to the signed-in
    // customer's wishlist (independent of whether it's in stock right now).
    const wishlistBtn = document.getElementById("sf-add-to-wishlist") as HTMLButtonElement | null;
    wishlistBtn?.addEventListener("click", async () => {
        const partId = parseInt(wishlistBtn.getAttribute("data-part-id") || "0", 10);
        const qty = Math.max(1, parseInt(qtyInput?.value || "1", 10) || 1);
        await toggleWishlist(wishlistBtn, partId, qty); // red heart = saved; clicking it again removes
    });

    // Related-product quick add (button lives inside the card link — stop navigation).
    for (const btn of Array.from(document.querySelectorAll<HTMLElement>(".sf-related-add"))) {
        btn.addEventListener("click", async (e) => {
            e.preventDefault();
            e.stopPropagation();
            const partId = parseInt(btn.getAttribute("data-part-id") || "0", 10);
            if (!partId) return;
            const check = await checkQuantity(partId, 1);
            if (check && (!check.Success || check.IsOutOfStock)) return;
            try {
                await addToCart(partId, 1);
            } catch (err) {
                showToast((err as Error).message, "error");
                return;
            }
            flash(btn, "✓ Added");
        });
    }

    // Related-product quick add-to-wishlist (same card-link stop-navigation concern).
    for (const btn of Array.from(document.querySelectorAll<HTMLButtonElement>('.sf-pdp-related [data-action="add-to-wishlist"]'))) {
        btn.addEventListener("click", async (e) => {
            e.preventDefault();
            e.stopPropagation();
            const partId = parseInt(btn.getAttribute("data-part-id") || "0", 10);
            await toggleWishlist(btn, partId, 1);
        });
    }
}

/**
 * The page rendered as in stock, but the live check at Add to Cart says it's gone: show the
 * (server-rendered, hidden) "Notify me when it's back" control under the buy row.
 */
function revealStockAlert(): void {
    const el = document.querySelector<HTMLElement>(".sf-pdp-buy > [data-stock-alert]");
    if (!el || !el.hidden) return;
    el.hidden = false;
    // On phones the buy row is pinned to the bottom; bring the control into view above it.
    el.scrollIntoView({ block: "nearest", behavior: "smooth" });
}

function init(): void {
    initGallery();
    initQty();
    initAddToCart();
    mountCartBadge();
    markSavedWishlistButtons(); // red heart if this part / a related part is already saved
    hydrateStockAlerts(); // "Notify me" — wires the buttons, restores a guest's state for this tab
    initRecentlyViewed();
}

/** Remember this view (#36991) and show the other recently viewed parts at the bottom. */
function initRecentlyViewed(): void {
    const partId = parseInt(document.querySelector<HTMLElement>(".sf-pdp")?.getAttribute("data-part-id") || "0", 10);
    if (partId > 0) recordView(partId);
    const host = document.querySelector<HTMLElement>("[data-recently-viewed]");
    if (host) mountRecentlyViewed(host, partId > 0 ? [partId] : []);
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
} else {
    init();
}
